/**
 * @format
 *
 * What is hashing?
 *
 * Hashing is the process of converting an input of any length into a fixed
 * size string of text using a mathematical function. The value to be
 * hashed is `input`, the algorithm used to do so is called a `hash` function.
 * The output is called the `hash value`.
 *
 * The same input should always result in the same unique hash value.
 * When it does not, a collision may occur.
 *
 * murmur or murmurhash is a modern non-cryptographic hash function with a low collision rate
 * and high performance. It is suitable for general hash-based lookups and unsuitable
 * for cryptographic uses. The name is composed of multiply (MU) and rotate (R), used in
 * its inner loop. All murmurhash versions are public domain software.
 *
 * @see https://observablehq.com/@kalebdf/a-b-testing-deterministic-bucketing
 */
import { murmur3 } from "murmurhash-js";

const TOTAL_BUCKETS = 10000;

/**
 * Use the murmurhash v3 hashing algorithm to generate a hash value for a given key
 * @param {String} key - a unique string
 * @param {Number} [seed=8426] - a means for randomizing the hash function
 * @returns {Number} - hashed value
 */
function getHashValue(key, seed = 8426) {
  const hash = murmur3(key, seed);
  return hash;
}

/**
 * Place a number in a bucket
 * @param {Number} key
 * @returns {Number} - the bucket (x of TOTAL_BUCKETS)
 */
function getBucket(key) {
  // Divide the key (hash value) by the largest possible value, integer 2^32
  const ratio = key / Math.pow(2, 32);
  // Use the ratio to divide up TOTAL_BUCKETS. Each key will be grouped
  // into a bucket 0 to TOTAL_BUCKETS
  const bucket = parseInt(ratio * TOTAL_BUCKETS, 10);
  return bucket;
}

/**
 * For a given bucket, return the variant id that
 * corresponds to the bucket. If there is no corresponding
 * variant then return null (user is not yet in test, likely because
 * their bucket falls out of the total audience allocation range)
 * @param {Number} bucketId
 * @param {Object} allocations
 * @returns {Number|null}
 */
function getVariant(bucketId, allocations) {
  for (let allocation of allocations) {
    if (bucketId < allocation.rangeEnd) {
      return allocation.id;
    }
  }
  return null;
}

/**
 * Create bucket ranges for a percentage of audience
 * @param {Object} p - props
 * @param {String} [p.variants=[]] - test variants
 * @param {Number} [p.percentage=100] - total audience allocation
 * @returns {Array} of variants
 */
function allocateAudience({ variants = [], percentage = 100 }) {
  let cummulativePercentage = 0;
  return variants.map(v => {
    cummulativePercentage += v.percentage / 100;
    return {
      id: v.id,
      percentage: v.percentage,
      rangeEnd: cummulativePercentage * ((TOTAL_BUCKETS * percentage) / 100)
    };
  });
}

const defaultAudienceAllocation = [
  { id: "control", percentage: 50 },
  { id: "variant", percentage: 50 }
];

export function configureTest(props) {
  const { name = "", uuid = "", percentage = 100, allocations = defaultAudienceAllocation } = props;
  const variants = allocateAudience({
    variants: allocations,
    percentage
  });
  const hashKey = `${name}@${percentage}:${uuid}`;
  const hashValue = getHashValue(hashKey);
  const bucket = getBucket(hashValue);
  const variant = getVariant(bucket, variants);
  const test = {
    name,
    allocations,
    percentage,
    variants,
    hashKey,
    hashValue,
    bucket,
    variant
  };
  return test;
}
